/**
 * Stale Apps Component for Profile360
 * Displays stale applications for a user with risk indicators
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent
} from '@mui/material';
import { styled } from '@mui/material/styles';
import profile360Service from '../services/profile360Service';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(1),
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
}));

// Risk indicator component for stale apps count
const RiskIndicator = styled(Box)(({ theme, $riskLevel }) => ({
  width: '100%',
  height: 8,
  borderRadius: 4,
  backgroundColor: $riskLevel === 'high' ? '#e74c3c' :
                   $riskLevel === 'medium' ? '#f39c12' : '#27ae60',
  marginTop: theme.spacing(1),
}));

// Statistics styled components removed - keeping it simple

/**
 * Stale Apps Component
 */
const StaleAppsComponent = ({ userEmail, onDataLoad }) => {
  const [staleApps, setStaleApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const onDataLoadRef = useRef(onDataLoad);

  // Update ref when onDataLoad changes
  useEffect(() => {
    onDataLoadRef.current = onDataLoad;
  }, [onDataLoad]);

  // Risk level calculation removed - not defined yet for stale apps

  /**
   * Format date string
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Statistics calculation removed - keeping it simple

  /**
   * Calculate risk level based on stale app count
   * More than 2 = High Risk (Red)
   * 1-2 = Medium Risk (Yellow)
   * 0 = No Risk (Green)
   */
  const calculateRiskLevel = (staleAppCount) => {
    if (staleAppCount > 2) return 'high';
    if (staleAppCount >= 1) return 'medium';
    return 'low'; // No stale apps
  };

  /**
   * Get risk level message
   */
  const getRiskMessage = (staleAppCount) => {
    if (staleAppCount > 2) return `High Risk: ${staleAppCount} stale applications detected`;
    if (staleAppCount >= 1) return `Medium Risk: ${staleAppCount} stale application${staleAppCount > 1 ? 's' : ''} detected`;
    return 'No Risk: No stale applications found';
  };

  /**
   * Fetch stale apps data
   */
  const fetchStaleApps = useCallback(async () => {
    if (!userEmail) return;

    console.log('📡 StaleAppsComponent: Fetching stale apps for user:', userEmail);
    setLoading(true);
    setError(null);

    try {
      const response = await profile360Service.getStaleAppsForUser(userEmail);

      if (response.success && response.data) {
        const apps = Array.isArray(response.data) ? response.data : [];
        setStaleApps(apps);

        // Notify parent component about data load
        if (onDataLoadRef.current) {
          onDataLoadRef.current({
            type: 'stale_apps',
            count: apps.length,
            riskLevel: calculateRiskLevel(apps.length),
            data: apps
          });
        }
      } else {
        throw new Error(response.error || 'Failed to fetch stale apps');
      }
    } catch (err) {
      console.error('Error fetching stale apps:', err);
      setError(err.message);
      setStaleApps([]);
    } finally {
      setLoading(false);
    }
  }, [userEmail]); // Remove onDataLoad from dependencies to prevent infinite re-renders

  // Effect to fetch data when userEmail changes
  useEffect(() => {
    console.log('🔄 StaleAppsComponent: userEmail changed to:', userEmail);
    if (userEmail) {
      fetchStaleApps();
    }
  }, [fetchStaleApps, userEmail]);

  // Don't render if no user email
  if (!userEmail) {
    return null;
  }

  return (
    <StyledCard>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Stale Access Analysis
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Applications with inactive access for user: {userEmail}
        </Typography>

        {loading && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress size={24} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            {/* Statistics section removed - keeping it simple */}

            {/* Risk Indicator */}
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Risk Assessment
              </Typography>
              <RiskIndicator $riskLevel={calculateRiskLevel(staleApps.length)} />
              <Typography variant="caption" color="text.secondary" mt={1} display="block">
                {getRiskMessage(staleApps.length)}
              </Typography>
            </Box>

            {/* Application Details */}
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Application Details
              </Typography>
            </Box>

            {/* Stale Apps Table */}
            {staleApps.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Application</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Last Access</TableCell>
                      <TableCell>Days Stale</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {staleApps.map((app, index) => (
                      <TableRow key={app.app_id || index}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {app.app_name || 'Unknown App'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {app.application_type_name || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(app.last_accessed)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {app.days_since_access || 0} days
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="success" sx={{ mt: 2 }}>
                No stale applications found for this user.
              </Alert>
            )}
          </>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default StaleAppsComponent;
